import { useMemo } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/widgets/carousel';
import { getCenterSlideIndex } from '@/shared/utils/carouselUtils';

interface TrendingItem {
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  bgColor?: string;
  imgBgColor?: string;
  position?: number;
  lazyload?: string;
  shouldRefresh?: boolean;
}

interface TrendingSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    layoutRows?: number;
    blankImageUrl?: string;
    items?: TrendingItem[];
    defautItemPosition?: number;
    showTitle?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function TrendingSection({
  data,
}: TrendingSectionProps) {
  const items = useMemo(() => (data.items || []) as TrendingItem[], [data.items]);
  const title = data.title?.[0] || '';
  const showTitle = data.showTitle !== false;

  // Calculate initial slide index (center slide)
  const initialSlideIndex = useMemo(() => getCenterSlideIndex(items.length), [items.length]);

  if (!items || items.length === 0) {
    return null;
  }

  const renderCard = (item: TrendingItem, index: number) => (
    <AppLink
      key={`${item.title}-${index}`}
      to={item.link}
      className="block relative group hover:opacity-90 transition-opacity overflow-hidden p-[10px] sm:pb-[20px] md:pb-[20px] h-full"
      style={{ 
        backgroundColor: item.bgColor || '#FEEEF1',
        borderRadius: '17px',
      }}
      eventName="Trend Spotting Clicked"
      eventProperties={{ 'Card Name': item.title }}
      shouldRefresh={item.shouldRefresh}
    >
      {/* Image */}
      <div 
        className="relative w-full mb-4 rounded-lg overflow-hidden flex items-center justify-center"
        style={{ 
          backgroundColor: item.imgBgColor || '#FFFFFF',
          aspectRatio: '186/204',
        }}
      >
        <img
          className="w-full h-full object-contain"
          src={item.imageUrl}
          alt={item.title}
          loading={item.lazyload ? 'lazy' : 'eager'}
          decoding="async"
        />
      </div>

      {/* Title */}
      <span className="block mb-2">
        <h3 className="inline-block relative pb-0.5 text-[12px] md:text-[14px] lg:text-[18px] md:leading-[16px] lg:leading-[22px] font-normal m-0 font-primary text-brand h-title-underline">
          {item.title}
        </h3>
      </span>

      {/* Description */}
      <p className="text-sm md:text-[12px] lg:text-[15px] text-[#927D7D] leading-relaxed md:leading-[16px] lg:leading-[16px]">
        {item.description}
      </p>
    </AppLink>
  );

  return (
    <section className="py-8 md:pt-[10px] md:py-0 w-full md:h-full md:flex md:flex-col">
      {/* Title - hidden on desktop */}
      {showTitle && title && (
        <div className="container-fluid md:hidden">
          <h2 className="mb-4 text-base font-normal text-center leading-normal">
            <span className="font-primary text-[#9D7792]">{title}</span>
          </h2>
        </div>
      )}

      {/* Mobile: Carousel - centered items */}
      <div className="md:hidden w-full">
        <Carousel
          opts={{
            align: 'center',
            loop: false,
            slidesToScroll: 1,
            containScroll: false,
            startIndex: initialSlideIndex,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-0 gap-2.5">
            {items.map((item, index) => (
              <CarouselItem
                key={`${item.title}-${index}`}
                className="pl-0 basis-auto flex-shrink-0 w-[80vw] min-w-[50vw] max-w-[50vw]"
              >
                {renderCard(item, index)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {/* Desktop: Static grid - all items visible */}
      <div className="hidden md:flex md:gap-4 container-fluid md:flex-1">
        {items.map((item, index) => (
          <div key={`${item.title}-${index}`} className="flex-1 w-1/3 flex flex-col">
            {renderCard(item, index)}
          </div>
        ))}
      </div>
    </section>
  );
}

